import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleDashboardRedirect } from './components/RoleDashboardRedirect';

// Auth Pages
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { PendingApprovalPage } from './pages/auth/PendingApprovalPage';
import { AcceptInvitationPage } from './pages/auth/AcceptInvitationPage';

const lazyNamed = (load, name) => lazy(async () => {
  const module = await load();
  const component = (name && module[name]) || module.default || module;
  return { default: component };
});

// Role and document screens are loaded only when their route is opened.
const EngineerDashboard = lazyNamed(() => import('./pages/engineer/EngineerDashboard'), 'EngineerDashboard');
const ProcurementDashboard = lazyNamed(() => import('./pages/procurement/ProcurementDashboard'), 'ProcurementDashboard');
const StoreDashboard = lazyNamed(() => import('./pages/store/StoreDashboard'), 'StoreDashboard');
const AccountDashboard = lazyNamed(() => import('./pages/accountant/AccountDashboard'), 'AccountDashboard');
const BOQPage = lazyNamed(() => import('./pages/shared/BOQPage'), 'BOQPage');
const IndentPage = lazyNamed(() => import('./pages/engineer/IndentPage'), 'IndentPage');
const PurchaseOrderPage = lazyNamed(() => import('./pages/procurement/PurchaseOrderPage'), 'PurchaseOrderPage');
const DeliveryChallanPage = lazyNamed(() => import('./pages/shared/DeliveryChallanPage'), 'DeliveryChallanPage');
const GRNPage = lazyNamed(() => import('./pages/shared/GRNPage'), 'GRNPage');
const MaterialsPage = lazyNamed(() => import('./pages/shared/MaterialsPage'), 'MaterialsPage');
const InventoryPage = lazyNamed(() => import('./pages/shared/InventoryPage'), 'InventoryPage');
const ProjectsPage = lazyNamed(() => import('./pages/shared/ProjectsPage'), 'ProjectsPage');
const ClientsPage = lazyNamed(() => import('./pages/shared/ClientsPage'), 'ClientsPage');
const SuppliersPage = lazyNamed(() => import('./pages/shared/SuppliersPage'), 'SuppliersPage');
const UsersPage = lazyNamed(() => import('./pages/shared/UsersPage'), 'UsersPage');
const ExtractionPage = lazyNamed(() => import('./pages/shared/ExtractionPage'), 'ExtractionPage');
const SettingsPage = lazyNamed(() => import('./pages/shared/SettingsPage'), 'SettingsPage');
const InvoicesPage = lazyNamed(() => import('./pages/accountant/InvoicesPage'), 'InvoicesPage');
const SuperAdminDashboard = lazy(() => import('./pages/super-admin/SuperAdminDashboard'));
const SuperAdminCompaniesPage = lazy(() => import('./pages/super-admin/SuperAdminCompaniesPage'));
const CompanyDetailsPage = lazy(() => import('./pages/super-admin/CompanyDetailsPage'));
const AdminDashboardEnhanced = lazy(() => import('./pages/admin/AdminDashboardEnhanced'));
const InviteUsersPage = lazy(() => import('./pages/administration/InviteUsersPage'));
const IndentFormPage = lazy(() => import('./pages/engineer/IndentFormPage'));
const BOQFormPage = lazyNamed(() => import('./pages/shared/BOQFormPage'), 'BOQFormPage');
const PurchaseOrderFormPage = lazyNamed(() => import('./pages/procurement/PurchaseOrderFormPage'), 'PurchaseOrderFormPage');
const ApprovalCenterPage = lazy(() => import('./pages/admin/ApprovalCenterPage'));
const BuyInquiryPage = lazyNamed(() => import('./pages/shared/BuyInquiryPage'), 'BuyInquiryPage');
const StoreMasterSheetPage = lazy(() => import('./pages/store/StoreMasterSheetPage'));
const StoreApprovedIndentDispatchPage = lazy(() => import('./pages/store/StoreApprovedIndentDispatchPage'));
const ScrapMaterialsPage = lazy(() => import('./pages/store/ScrapMaterialsPage'));
const QuotationPage = lazy(() => import('./pages/procurement/QuotationPage'));
const ProcurementBuyInquiryPage = lazy(() => import('./pages/procurement/ProcurementBuyInquiryPage'));
const InvoiceVerificationPage = lazy(() => import('./pages/engineer/InvoiceVerificationPage'));
const ReturnedStockPage = lazy(() => import('./pages/engineer/ReturnedStockPage'));
const AdministrationInvoiceRegisterPage = lazy(() => import('./pages/administration/AdministrationInvoiceRegisterPage'));
const AdministrationProjectsPage = lazy(() => import('./pages/administration/AdministrationProjectsPage'));
const AssignedProjectsPage = lazy(() => import('./pages/administration/AssignedProjectsPage'));
const AdministrationProjectDetailPage = lazy(() => import('./pages/administration/AdministrationProjectDetailPage'));
const AdministrationProjectEditPage = lazy(() => import('./pages/administration/AdministrationProjectEditPage'));
const GRNFormPage = lazy(() => import('./pages/shared/GRNFormPage'));
const DocumentDetailPage = lazy(() => import('./pages/shared/DocumentDetailPage'));
const AdminBOQApprovalPage = lazyNamed(() => import('./pages/admin/AdminBOQApprovalPage'), 'AdminBOQApprovalPage');
const AdminIndentApprovalPage = lazyNamed(() => import('./pages/admin/AdminIndentApprovalPage'), 'AdminIndentApprovalPage');
const AdminBuyInquiryApprovalPage = lazy(() => import('./pages/admin/AdminBuyInquiryApprovalPage'));
const AdminQuotationApprovalPage = lazy(() => import('./pages/admin/AdminQuotationApprovalPage'));
const AdminPOApprovalPage = lazy(() => import('./pages/admin/AdminPOApprovalPage'));
const POApiTestPage = lazy(() => import('./pages/procurement/POApiTestPage'));

import { Toaster } from './components/ui/toaster';
import './App.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center bg-slate-50">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
            </div>
          }
        >
        <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

          {/* Protected Routes */}
          <Route
            path="/pending-approval"
            element={
              <ProtectedRoute allowPendingApproval>
                <PendingApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleDashboardRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq"
            element={
              <ProtectedRoute>
                <BOQPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq/AdminApproval"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AdminBOQApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/boq/create"
            element={
              <ProtectedRoute>
                <BOQFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/indent"
            element={
              <ProtectedRoute>
                <IndentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/indent/create"
            element={
              <ProtectedRoute>
                <IndentFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/indent/:indentId/edit"
            element={
              <ProtectedRoute allowedRoles={['PROJECT_ENGINEER']}>
                <IndentFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/indent/AdminApproval"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AdminIndentApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-order"
            element={
              <ProtectedRoute>
                <PurchaseOrderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-order/create"
            element={
              <ProtectedRoute allowedRoles={['PROCUREMENT', 'ADMIN', 'SUPER_ADMIN']}>
                <Navigate to="/quotations" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations"
            element={
              <ProtectedRoute allowedRoles={['PROCUREMENT', 'ADMIN', 'SUPER_ADMIN']}>
                <QuotationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotations/AdminApproval"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AdminQuotationApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-order/AdminApproval"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AdminPOApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/po-api-test"
            element={
              <ProtectedRoute allowedRoles={['PROCUREMENT', 'ADMIN', 'SUPER_ADMIN']}>
                <POApiTestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement/buy-inquiries"
            element={
              <ProtectedRoute allowedRoles={['PROCUREMENT', 'ADMIN', 'SUPER_ADMIN']}>
                <ProcurementBuyInquiryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buy-inquiry"
            element={
              <ProtectedRoute allowedRoles={['STORE', 'ADMIN', 'SUPER_ADMIN']}>
                <BuyInquiryPage />
              </ProtectedRoute>
            }
          />
          
        <Route
            path="/buy-inquiry/AdminApproval"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AdminBuyInquiryApprovalPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/delivery-challan"
            element={
              <ProtectedRoute>
                <DeliveryChallanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grn"
            element={
              <ProtectedRoute>
                <GRNPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/grn/create"
            element={
              <ProtectedRoute allowedRoles={['STORE', 'PROJECT_ENGINEER', 'ADMIN', 'SUPER_ADMIN']}>
                <GRNFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/materials"
            element={
              <ProtectedRoute>
                <MaterialsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <InventoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/master-material-sheet"
            element={
              <ProtectedRoute allowedRoles={['STORE', 'ADMIN', 'SUPER_ADMIN']}>
                <StoreMasterSheetPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/approved-indent-dispatch"
            element={
              <ProtectedRoute allowedRoles={['STORE', 'ADMIN', 'SUPER_ADMIN']}>
                <StoreApprovedIndentDispatchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store/scrap"
            element={
              <ProtectedRoute allowedRoles={['STORE', 'ADMIN', 'SUPER_ADMIN']}>
                <ScrapMaterialsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute allowedRoles={['ADMINISTRATION', 'ADMIN', 'SUPER_ADMIN']}>
                <ClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administration/projects"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'ADMINISTRATION', 'SUPER_ADMIN']}>
                <AdministrationProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administration/projects/:projectId"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'ADMINISTRATION', 'SUPER_ADMIN']}>
                <AdministrationProjectDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administration/projects/:projectId/edit"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'ADMINISTRATION', 'SUPER_ADMIN']}>
                <AdministrationProjectEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <SuppliersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/extraction"
            element={
              <ProtectedRoute>
                <ExtractionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute allowedRoles={['PROJECT_ENGINEER', 'STORE', 'ACCOUNT', 'ADMINISTRATION', 'ADMIN', 'SUPER_ADMIN']}>
                <InvoicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/verify"
            element={
              <ProtectedRoute allowedRoles={['PROJECT_ENGINEER', 'ACCOUNT', 'ADMINISTRATION', 'ADMIN', 'SUPER_ADMIN', 'STORE']}>
                <InvoiceVerificationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/administration"
            element={
              <ProtectedRoute allowedRoles={['ADMINISTRATION', 'ADMIN', 'SUPER_ADMIN']}>
                <AdministrationInvoiceRegisterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administration/assigned-projects"
            element={
              <ProtectedRoute allowedRoles={['ADMINISTRATION', 'ADMIN', 'SUPER_ADMIN']}>
                <AssignedProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/engineer/returned-stock"
            element={
              <ProtectedRoute allowedRoles={['PROJECT_ENGINEER', 'ADMIN', 'SUPER_ADMIN']}>
                <ReturnedStockPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/approval-center"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <ApprovalCenterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/:type/:id"
            element={
              <ProtectedRoute>
                <DocumentDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Role-based Dashboards */}
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <AdminDashboardEnhanced />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administration-dashboard"
            element={
              <ProtectedRoute allowedRoles={['ADMINISTRATION', 'ADMIN']}>
                <AdminDashboardEnhanced />
              </ProtectedRoute>
            }
          />
          <Route
            path="/engineer-dashboard"
            element={
              <ProtectedRoute allowedRoles={['PROJECT_ENGINEER']}>
                <EngineerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procurement-dashboard"
            element={
              <ProtectedRoute allowedRoles={['PROCUREMENT']}>
                <ProcurementDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/store-dashboard"
            element={
              <ProtectedRoute allowedRoles={['STORE']}>
                <StoreDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account-dashboard"
            element={
              <ProtectedRoute allowedRoles={['ACCOUNT']}>
                <AccountDashboard />
              </ProtectedRoute>
            }
          />

          {/* Super Admin & Invitation Routes */}
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/companies"
            element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                <SuperAdminCompaniesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/company/:companyId"
            element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                <CompanyDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invite-users"
            element={
              <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'ADMINISTRATION']}>
                <InviteUsersPage />
              </ProtectedRoute>
            }
          />

          {/* Default Routes */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
        <Toaster />
      </AuthProvider>
    </Router>
  );
}

export default App;
